<template>
  <b-modal
    v-model="showModal"
    id="game-form-modal"
    :title="editMode ? 'Edit game' : 'Create a game'"
    size="xl"
    :ok-title="loading ? '' : editMode ? 'Edit game' : 'Create game'"
    cancel-title="Cancel"
    ok-variant="success"
    :cancel-disabled="loading"
    @hidden="$emit('hidden')"
    @ok="handleSubmit">
    <template #modal-ok>
      <div class="d-flex align-items-center" style="min-width: 108px; justify-content: center">
        <b-spinner small v-if="loading"></b-spinner>
        <span v-else>{{ editMode ? 'Edit game' : 'Create game' }}</span>
      </div>
    </template>

    <b-container fluid>
      <!-- Alerte si des modifications n'ont pas été sauvegardées -->
      <b-alert
        show
        variant="warning"
        :style="{
          opacity: hasUnsavedChanges ? 1 : 0,
        }">
        Unsaved changes
      </b-alert>

      <!-- ERRORS -->
      <b-alert v-if="Object.keys(errors).length > 0" show variant="danger">
        <div class="h5">Errors occurred:</div>
        <ul>
          <li v-for="(error, field) in errors" :key="field">
            <!-- {{ field }}: {{ error.msg }} -->
            {{ error.msg }}
          </li>
        </ul>
      </b-alert>

      <!-- GAME FORM -->
      <b-card no-body class="">
        <b-tabs content-class="mt-3" justified card>
          <!-- TAB: General -->
          <b-tab lazy title="General">
            <b-card-title>General</b-card-title>
            <hr class="pb-5" />

            <b-card-text>
              <!-- Jeu par défaut -->
              <b-form-group label="Default game" label-cols="3">
                <select v-model="game.isDefault" id="isDefault" class="form-control form-control-sm">
                  <option :value="true">Default</option>
                  <option :value="false">Custom</option>
                </select>
              </b-form-group>

              <!-- Langue -->
              <b-form-group label="Language" label-cols="3">
                <b-form-select
                  v-model="game.language"
                  :options="languageOptions"
                  class="form-control-sm"></b-form-select>
              </b-form-group>

              <!-- Nom du jeu -->
              <b-form-group label="Game name" label-cols="3">
                <input type="text" v-model="game.name" class="form-control form-control-sm" />
              </b-form-group>

              <!-- Description du jeu -->
              <b-form-group label="Game description" label-cols="3">
                <textarea v-model="game.description" class="form-control" id="description" rows="3" />
              </b-form-group>

              <!-- Afficher le nom -->
              <b-form-group label="Display name" label-cols="3">
                <b-form-checkbox v-model="game.display_name" id="display_name" name="display_name" switch>
                  {{ game.display_name ? 'Game name is displayed' : 'Game name is hidden' }}
                </b-form-checkbox>
              </b-form-group>

              <!-- Catégories questions -->
              <b-form-group label="Question categories" label-cols="3">
                <div class="h5">
                  <b-badge
                    v-if="game.themes.length === 0"
                    variant="danger"
                    class="d-inline-flex align-items-center mr-2">
                    No category selected
                  </b-badge>

                  <b-badge
                    v-for="theme in game.themes"
                    :key="theme"
                    variant="info"
                    class="d-inline-flex align-items-center mr-2">
                    {{ theme }}
                    <b-icon-x
                      @click="removeTheme(theme)"
                      class="ml-1"
                      style="cursor: pointer; width: 18px; height: 18px"></b-icon-x>
                  </b-badge>
                </div>
                <vue-typeahead-bootstrap
                  ref="typeaheadThemes"
                  inputName="themes"
                  :disabled="availableThemes.length === 0"
                  :showOnFocus="true"
                  :data="availableThemes"
                  :maxMatches="1000"
                  class="w-100"
                  v-model="typeaheadThemeModel"
                  @hit="addTheme($event)" />
              </b-form-group>

              <!-- Niveau imposé -->
              <b-form-group label="Required level" label-cols="3">
                <select v-model="game.defaultLevel" id="default_level_" class="form-control form-control-sm">
                  <option value="">None</option>
                  <option v-for="level in levels" :key="'default_level_' + '_' + level.value" :value="level.value">
                    {{ level.label }}
                  </option>
                </select>
              </b-form-group>

              <!-- Audio au lancement du jeu -->
              <b-form-group label="Game launch audio" label-cols="3">
                <AudioSelector
                  :initial-audio="{
                    name: game.audio_launch_url,
                    path: game.audio_launch_url_url,
                  }"
                  buttons-position="right"
                  empty-container-action="import"
                  :disabled="loading"
                  @file-uploaded="onAudioUploaded($event)"
                  @file-selected="
                    onFileSelected($event, 'audio_launch_url', 'audio_launch_url_path', 'audio_launch_url_url')
                  " />
              </b-form-group>

              <!-- Intro audio -->
              <b-form-group label="Intro audio" label-cols="3">
                <AudioSelector
                  :initial-audio="{
                    name: game.audio,
                    path: game.audioUrl,
                  }"
                  buttons-position="right"
                  empty-container-action="import"
                  :disabled="loading"
                  @file-uploaded="onAudioUploaded($event)"
                  @file-selected="onFileSelected($event, 'audio', 'audioPath', 'audioUrl')" />
              </b-form-group>
            </b-card-text>
          </b-tab>

          <!-- TAB: Manches -->
          <b-tab lazy title="Rounds">
            <b-card-title>Rounds</b-card-title>
            <hr class="pb-0" />

            <b-card-text>
              <b-row>
                <!--
                Colonne de navigation entre les manches,
                avec possibilité de drag & drop pour réorganiser l'ordre des manches
                -->
                <b-col cols="12" md="4" lg="4">
                  <b-nav pills vertical class="col-auto bg-light p-3">
                    <!-- faux nav-item quand on a aucune manche -->
                    <b-nav-item
                      v-if="!game.handles || game.handles.length === 0"
                      disabled
                      class="position-absolute flex-fill">
                      <b-icon-chevron-expand class="draggable-icon mr-2"></b-icon-chevron-expand>
                      <span class="tab-title">No round</span>
                    </b-nav-item>

                    <draggable
                      tag="div"
                      id="draggable_handle"
                      style="min-height: 200px"
                      v-model="game.handles"
                      v-bind="dragOptions"
                      group="handle"
                      @start="dragStart"
                      @end="dragEnd"
                      :disabled="!game.handles || game.handles.length === 0">
                      <transition-group type="transition" :name="!drag ? 'flip-list' : null">
                        <div
                          v-for="(handle, index) in game.handles"
                          :key="'handle__' + handle.id + '__nav'"
                          class="d-flex justify-content-between align-items-center">
                          <b-nav-item @click="handleTab = index" :active="handleTab === index" class="flex-fill">
                            <b-icon-chevron-expand class="draggable-icon mr-1"></b-icon-chevron-expand>
                            <span class="tab-title">
                              {{ index + 1 }} -
                              {{ handle.type }}
                            </span>
                          </b-nav-item>

                          <!-- Bouton Supprimer la manche -->
                          <b-button @click="deleteHandle(index)" variant="none" class="ml-2" size="sm">
                            <b-icon-trash-fill variant="danger" />
                          </b-button>
                        </div>
                      </transition-group>
                    </draggable>

                    <hr class="w-100" />

                    <!-- Bouton ajouter une manche -->
                    <b-dropdown text="Add a round" class="mt-2" variant="success" menu-class="w-100 text-center">
                      <b-dropdown-item-button
                        v-for="handleName in playableHandleTypes"
                        :key="'handleName_' + handleName"
                        @click="addHandle(handleName)">
                        {{ handlesTypeWithName[handleName] }}
                      </b-dropdown-item-button>
                      <b-dropdown-divider></b-dropdown-divider>
                      <b-dropdown-item-button
                        v-for="handleName in unplayableHandleTypes"
                        :key="'handleName_' + handleName"
                        @click="addHandle(handleName)">
                        {{ handlesTypeWithName[handleName] }}
                      </b-dropdown-item-button>
                    </b-dropdown>
                  </b-nav>
                </b-col>

                <!-- Colonne de contenu de la manche selectionnée -->
                <b-col>
                  <div v-if="game.handles.length === 0">
                    <b-alert show variant="info">No round. Please add at least one round.</b-alert>
                  </div>

                  <b-tabs pills card vertical :value="handleTab" nav-class="d-none">
                    <b-tab v-for="(handle, index) in game.handles" :key="'handle__' + handle.id + '__content'">
                      <b-card-title>Round {{ index + 1 }}/{{ game.handles.length }} - {{ handle.type }}</b-card-title>
                      <hr class="pb-4" />

                      <HandleCard
                        :value="game.handles[index]"
                        :index="index"
                        :gameId="game._id || 'new'"
                        :themes="themes"
                        :videos="videos"
                        @input="handleInput(index, $event)"
                        @typeChanged="handleTypeChanged"
                        @deleteHandle="deleteHandle"></HandleCard>
                    </b-tab>
                  </b-tabs>
                </b-col>
              </b-row>
            </b-card-text>
          </b-tab>

          <!-- TAB: Images par défaut -->
          <b-tab lazy title="Default images">
            <b-card-title>Default images</b-card-title>
            <hr class="pb-5" />

            <b-card-text>
              <!-- Image de présentation -->
              <b-form-group label="Intro audio" label-cols="3">
                <ImageSelector
                  :initial-image="{
                    name: game.presentation_image_name,
                    path: game.presentation_image_url_url,
                  }"
                  buttons-position="right"
                  empty-container-action="import"
                  :disabled="loading"
                  @file-uploaded="onImageUploaded($event)"
                  @file-selected="
                    onFileSelected(
                      $event,
                      'presentation_image_name',
                      'presentation_image_url',
                      'presentation_image_url_url'
                    )
                  " />
              </b-form-group>

              <!-- Ecran borne -->
              <b-form-group label="Terminal screen" label-cols="3">
                <ImageSelector
                  :initial-image="{
                    name: game.deskDisplayImage,
                    path: game.deskDisplayImageUrl,
                  }"
                  buttons-position="right"
                  empty-container-action="import"
                  :disabled="loading"
                  @file-uploaded="onImageUploaded($event)"
                  @file-selected="
                    onFileSelected($event, 'deskDisplayImage', 'deskDisplayImagePath', 'deskDisplayImageUrl')
                  " />
              </b-form-group>

              <!-- Ecrans défaut -->
              <b-form-group label="Default screens" label-cols="3">
                <ImageSelector
                  :initial-image="{
                    name: game.defaultDisplay,
                    path: game.defaultDisplayUrl,
                  }"
                  buttons-position="right"
                  empty-container-action="import"
                  :disabled="loading"
                  @file-uploaded="onImageUploaded($event)"
                  @file-selected="
                    onFileSelected($event, 'defaultDisplay', 'defaultDisplayPath', 'defaultDisplayUrl')
                  " />
              </b-form-group>

              <!-- Ecrans questions -->
              <b-form-group label="Question screens" label-cols="3">
                <ImageSelector
                  :initial-image="{
                    name: game.questionDisplay,
                    path: game.questionDisplayUrl,
                  }"
                  buttons-position="right"
                  empty-container-action="import"
                  :disabled="loading"
                  @file-uploaded="onImageUploaded($event)"
                  @file-selected="
                    onFileSelected($event, 'questionDisplay', 'questionDisplayPath', 'questionDisplayUrl')
                  " />
              </b-form-group>

              <!-- Ecrans scores -->
              <b-form-group label="Score screens" label-cols="3">
                <ImageSelector
                  :initial-image="{
                    name: game.scoreDisplay,
                    path: game.scoreDisplayUrl,
                  }"
                  buttons-position="right"
                  empty-container-action="import"
                  :disabled="loading"
                  @file-uploaded="onImageUploaded($event)"
                  @file-selected="onFileSelected($event, 'scoreDisplay', 'scoreDisplayPath', 'scoreDisplayUrl')" />
              </b-form-group>
            </b-card-text>
          </b-tab>

          <!-- TAB: Textes -->
          <b-tab lazy title="Texts">
            <b-card-title>Texts</b-card-title>
            <hr class="pb-5" />

            <!-- Texte attente des joueurs: waitOtherPlayers -->
            <b-form-group label="Waiting for players text" label-cols="3">
              <input type="text" v-model="game.waitOtherPlayers" class="form-control" id="waitOtherPlayers" />
            </b-form-group>

            <!-- Texte bouton attente: waitClickHereBtn -->
            <b-form-group label="Waiting button text" label-cols="3">
              <input type="text" v-model="game.waitClickHereBtn" class="form-control" id="waitClickHereBtn" />
            </b-form-group>

            <!-- Texte combattant: waitFighterLabel -->
            <b-form-group label="Fighter text" label-cols="3">
              <input type="text" v-model="game.waitFighterLabel" class="form-control" id="waitFighterLabel" />
            </b-form-group>

            <b-card-title class="mt-5">Jokers</b-card-title>
            <hr class="pb-5" />

            <!-- Texte jokers disponibles: availableJokers -->
            <b-form-group label="Available jokers" label-cols="3">
              <input type="text" v-model="game.availableJokers" class="form-control" id="availableJokers" />
            </b-form-group>

            <!-- Texte joker - pas de joker envoyé: noJokerSent -->
            <b-form-group label="Joker - no joker sent" label-cols="3">
              <input type="text" v-model="game.noJokerSent" class="form-control" id="noJokerSent" />
            </b-form-group>

            <!-- Texte joker - pas de joker reçu: noJokerReceived -->
            <b-form-group label="Joker - no joker received" label-cols="3">
              <input type="text" v-model="game.noJokerReceived" class="form-control" id="noJokerReceived" />
            </b-form-group>

            <!-- Texte joker - uppercut envoyé: uppercutSent -->
            <b-form-group label="Joker - uppercut sent" label-cols="3">
              <input type="text" v-model="game.uppercutSent" class="form-control" id="uppercutSent" />
            </b-form-group>

            <!-- Texte joker - uppercut reçu: uppercutReceived -->
            <b-form-group label="Joker - uppercut received" label-cols="3">
              <input type="text" v-model="game.uppercutReceived" class="form-control" id="uppercutReceived" />
            </b-form-group>

            <!-- Texte joker - pari envoyé: parierSent -->
            <b-form-group label="Joker - gamble sent" label-cols="3">
              <input type="text" v-model="game.parierSent" class="form-control" id="parierSent" />
            </b-form-group>

            <!-- Texte joker - pari reçu: parierReceived -->
            <b-form-group label="Joker - gamble received" label-cols="3">
              <input type="text" v-model="game.parierReceived" class="form-control" id="parierReceived" />
            </b-form-group>

            <!-- Texte joker - points volé: volerSent -->
            <b-form-group label="Joker - points stolen sent" label-cols="3">
              <input type="text" v-model="game.volerSent" class="form-control" id="volerSent" />
            </b-form-group>

            <!-- Texte joker - points volé reçu: volerReceived -->
            <b-form-group label="Joker - points stolen received" label-cols="3">
              <input type="text" v-model="game.volerReceived" class="form-control" id="volerReceived" />
            </b-form-group>

            <b-card-title class="mt-5">Advanced</b-card-title>
            <hr class="pb-5" />

            <!-- Titre tableau scores - Manche: questionScoresTableRound -->
            <b-form-group label="Title table scores - Round" label-cols="3">
              <input
                type="text"
                v-model="game.questionScoresTableRound"
                class="form-control"
                id="questionScoresTableRound" />
            </b-form-group>

            <!-- Titre tableau scores - Points gagnés: questionScoresTablePointsWin -->
            <b-form-group label="Title table scores - Points won" label-cols="3">
              <input
                type="text"
                v-model="game.questionScoresTablePointsWin"
                class="form-control"
                id="questionScoresTablePointsWin" />
            </b-form-group>

            <!-- Titre tableau scores - Points perdus: questionScoresTablePointsLost -->
            <b-form-group label="Title table scores - Points lost" label-cols="3">
              <input
                type="text"
                v-model="game.questionScoresTablePointsLost"
                class="form-control"
                id="questionScoresTablePointsLost" />
            </b-form-group>

            <!-- Titre tableau scores - Points total: questionScoresTablePointsTotal -->
            <b-form-group label="Title table scores - Total points" label-cols="3">
              <input
                type="text"
                v-model="game.questionScoresTablePointsTotal"
                class="form-control"
                id="questionScoresTablePointsTotal" />
            </b-form-group>

            <!-- Texte questions restantes: questionsLeft -->
            <b-form-group label="Text questions left" label-cols="3">
              <input type="text" v-model="game.questionsLeft" class="form-control" id="questionsLeft" />
            </b-form-group>

            <!-- Texte total des points: resultTotalPointsText -->
            <b-form-group label="Total points text" label-cols="3">
              <input type="text" v-model="game.resultTotalPointsText" class="form-control" id="resultTotalPointsText" />
            </b-form-group>

            <!-- Texte bouton vrai: trueText -->
            <b-form-group label="True button text" label-cols="3">
              <input type="text" v-model="game.trueText" class="form-control" id="trueText" />
            </b-form-group>

            <!-- Texte bouton faux: falseText -->
            <b-form-group label="False button text" label-cols="3">
              <input type="text" v-model="game.falseText" class="form-control" id="falseText" />
            </b-form-group>

            <!-- Texte pause: pauseText -->
            <b-form-group label="Pause text" label-cols="3">
              <input type="text" v-model="game.pauseText" class="form-control" id="pauseText" />
            </b-form-group>

            <!-- Texte bonne réponse: rightAnswer -->
            <b-form-group label="Correct answer text" label-cols="3">
              <input type="text" v-model="game.rightAnswer" class="form-control" id="rightAnswer" />
            </b-form-group>

            <!-- Texte mauvaise réponse: wrongAnswer -->
            <b-form-group label="Wrong answer text" label-cols="3">
              <input type="text" v-model="game.wrongAnswer" class="form-control" id="wrongAnswer" />
            </b-form-group>

            <!-- Texte bonne réponse alternative: rightAnswerAlt -->
            <b-form-group label="Alternative correct answer text" label-cols="3">
              <input type="text" v-model="game.rightAnswerAlt" class="form-control" id="rightAnswerAlt" />
            </b-form-group>

            <!-- Texte mauvaise réponse alternative: wrongAnswerAlt -->
            <b-form-group label="Alternative wrong answer text" label-cols="3">
              <input type="text" v-model="game.wrongAnswerAlt" class="form-control" id="wrongAnswerAlt" />
            </b-form-group>

            <!-- Texte réponse proche 1: almostAnswerOne -->
            <b-form-group label="Text almost answer 1" label-cols="3">
              <input type="text" v-model="game.almostAnswerOne" class="form-control" id="almostAnswerOne" />
            </b-form-group>

            <!-- Texte réponse proche 2: almostAnswerTwo -->
            <b-form-group label="Text almost answer 2" label-cols="3">
              <input type="text" v-model="game.almostAnswerTwo" class="form-control" id="almostAnswerTwo" />
            </b-form-group>

            <!-- Texte réponse proche 3: almostAnswerThree -->
            <b-form-group label="Text almost answer 3" label-cols="3">
              <input type="text" v-model="game.almostAnswerThree" class="form-control" id="almostAnswerThree" />
            </b-form-group>

            <!-- Texte blindtest - 1er: blindtestFirst -->
            <b-form-group label="Blindtest text - 1st" label-cols="3">
              <input type="text" v-model="game.blindtestFirst" class="form-control" id="blindtestFirst" />
            </b-form-group>

            <!-- Texte blindtest - 2eme: blindtestSecond -->
            <b-form-group label="Blindtest text - 2nd" label-cols="3">
              <input type="text" v-model="game.blindtestSecond" class="form-control" id="blindtestSecond" />
            </b-form-group>

            <!-- Texte blindtest - 3eme: blindtestThird -->
            <b-form-group label="Blindtest text - 3rd" label-cols="3">
              <input type="text" v-model="game.blindtestThird" class="form-control" id="blindtestThird" />
            </b-form-group>

            <!-- Texte blindtest - trop tard: blindtestTooLate -->
            <b-form-group label="Blindtest text - too late" label-cols="3">
              <input type="text" v-model="game.blindtestTooLate" class="form-control" id="blindtestTooLate" />
            </b-form-group>

            <!-- Texte Joker score - Points vainqueur: finalScoreJokerWinnerPoints -->
            <b-form-group label="Joker score text - Winner points" label-cols="3">
              <input
                type="text"
                v-model="game.finalScoreJokerWinnerPoints"
                class="form-control"
                id="finalScoreJokerWinnerPoints" />
            </b-form-group>

            <!-- Texte Joker score - Points perdant: finalScoreJokerLoserPoints -->
            <b-form-group label="Joker score text - Loser points" label-cols="3">
              <input
                type="text"
                v-model="game.finalScoreJokerLoserPoints"
                class="form-control"
                id="finalScoreJokerLoserPoints" />
            </b-form-group>

            <!-- Texte Joker score - Pas de score: finalScoreJokerNone -->
            <b-form-group label="Joker score text - No score" label-cols="3">
              <input type="text" v-model="game.finalScoreJokerNone" class="form-control" id="finalScoreJokerNone" />
            </b-form-group>
          </b-tab>

          <!-- TAB: Jokers -->
          <b-tab lazy title="Jokers">
            <b-card-title>Jokers</b-card-title>
            <hr class="pb-5" />

            <b-card-text>
              <b-tabs pills card vertical>
                <!-- Onglet General Joker -->
                <b-tab title="All jokers" key="joker__general">
                  <b-card-title>All jokers</b-card-title>
                  <hr class="pb-5" />

                  <b-card-text>
                    <!-- Image choix adversaire: opponentChoiceImage -->
                    <b-form-group label="Opponent choice image" label-cols="3">
                      <ImageSelector
                        :initial-image="{
                          name: game.opponentChoiceImage,
                          path: game.opponentChoiceImageUrl,
                        }"
                        buttons-position="right"
                        empty-container-action="import"
                        :disabled="loading"
                        @file-uploaded="onImageUploaded($event)"
                        @file-selected="
                          onFileSelected(
                            $event,
                            'opponentChoiceImage',
                            'opponentChoiceImagePath',
                            'opponentChoiceImageUrl'
                          )
                        " />
                    </b-form-group>

                    <!-- Image joker indisponible: defaultJokerUnavailableImage -->
                    <b-form-group label="Unavailable joker image" label-cols="3">
                      <ImageSelector
                        :initial-image="{
                          name: game.defaultJokerUnavailableImage,
                          path: game.defaultJokerUnavailableImageUrl,
                        }"
                        buttons-position="right"
                        empty-container-action="import"
                        :disabled="loading"
                        @file-uploaded="onImageUploaded($event)"
                        @file-selected="
                          onFileSelected(
                            $event,
                            'defaultJokerUnavailableImage',
                            'defaultJokerUnavailableImagePath',
                            'defaultJokerUnavailableImageUrl'
                          )
                        " />
                    </b-form-group>

                    <!-- Image pas de joker: noJokerImage -->
                    <b-form-group label="No joker image" label-cols="3">
                      <ImageSelector
                        :initial-image="{
                          name: game.noJokerImage,
                          path: game.noJokerImageUrl,
                        }"
                        buttons-position="right"
                        empty-container-action="import"
                        :disabled="loading"
                        @file-uploaded="onImageUploaded($event)"
                        @file-selected="
                          onFileSelected($event, 'noJokerImage', 'noJokerImagePath', 'noJokerImageUrl')
                        " />
                    </b-form-group>

                    <!-- Image de fond - Aucun point: pointNoneBackImage -->
                    <b-form-group label="Background image - No points" label-cols="3">
                      <ImageSelector
                        :initial-image="{
                          name: game.pointNoneBackImage,
                          path: game.pointNoneBackImageUrl,
                        }"
                        buttons-position="right"
                        empty-container-action="import"
                        :disabled="loading"
                        @file-uploaded="onImageUploaded($event)"
                        @file-selected="
                          onFileSelected(
                            $event,
                            'pointNoneBackImage',
                            'pointNoneBackImagePath',
                            'pointNoneBackImageUrl'
                          )
                        " />
                    </b-form-group>
                  </b-card-text>
                </b-tab>

                <!-- Onglet Joker Uppercut -->
                <b-tab title="Joker - Uppercut" key="joker__uppercut">
                  <b-card-title>Joker - Uppercut</b-card-title>
                  <hr class="pb-5" />

                  <b-card-text>
                    <!-- Nombre Uppercuts: defaultJokerUppercutCount -->
                    <b-form-group label="Number of Uppercuts" label-cols="3">
                      <input
                        type="number"
                        v-model="game.defaultJokerUppercutCount"
                        class="form-control form-control-sm" />
                    </b-form-group>

                    <!-- Image Joker - Uppercut: defaultJokerUppercutImage -->
                    <b-form-group label="Joker image - Uppercut" label-cols="3">
                      <ImageSelector
                        :initial-image="{
                          name: game.defaultJokerUppercutImage,
                          path: game.defaultJokerUppercutImageUrl,
                        }"
                        buttons-position="right"
                        empty-container-action="import"
                        :disabled="loading"
                        @file-uploaded="onImageUploaded($event)"
                        @file-selected="
                          onFileSelected(
                            $event,
                            'defaultJokerUppercutImage',
                            'defaultJokerUppercutImagePath',
                            'defaultJokerUppercutImageUrl'
                          )
                        " />
                    </b-form-group>

                    <!-- Image Utilisation Joker - Uppercut: jokerOverviewUppercutImage -->
                    <b-form-group label="Image use joker - Uppercut" label-cols="3">
                      <ImageSelector
                        :initial-image="{
                          name: game.jokerOverviewUppercutImage,
                          path: game.jokerOverviewUppercutImageUrl,
                        }"
                        buttons-position="right"
                        empty-container-action="import"
                        :disabled="loading"
                        @file-uploaded="onImageUploaded($event)"
                        @file-selected="
                          onFileSelected(
                            $event,
                            'jokerOverviewUppercutImage',
                            'jokerOverviewUppercutImagePath',
                            'jokerOverviewUppercutImageUrl'
                          )
                        " />
                    </b-form-group>

                    <!-- Image de fond - Uppercut: pointsUppercutBackImage -->
                    <b-form-group label="Background image - Uppercut" label-cols="3">
                      <ImageSelector
                        :initial-image="{
                          name: game.pointsUppercutBackImage,
                          path: game.pointsUppercutBackImageUrl,
                        }"
                        buttons-position="right"
                        empty-container-action="import"
                        :disabled="loading"
                        @file-uploaded="onImageUploaded($event)"
                        @file-selected="
                          onFileSelected(
                            $event,
                            'pointsUppercutBackImage',
                            'pointsUppercutBackImagePath',
                            'pointsUppercutBackImageUrl'
                          )
                        " />
                    </b-form-group>

                    <!-- Image de fond - Uppercut reçu: pointsUppercuttedBackImage -->
                    <b-form-group label="Background image - Uppercut received" label-cols="3">
                      <ImageSelector
                        :initial-image="{
                          name: game.pointsUppercuttedBackImage,
                          path: game.pointsUppercuttedBackImageUrl,
                        }"
                        buttons-position="right"
                        empty-container-action="import"
                        :disabled="loading"
                        @file-uploaded="onImageUploaded($event)"
                        @file-selected="
                          onFileSelected(
                            $event,
                            'pointsUppercuttedBackImage',
                            'pointsUppercuttedBackImagePath',
                            'pointsUppercuttedBackImageUrl'
                          )
                        " />
                    </b-form-group>
                  </b-card-text>
                </b-tab>

                <!-- Onglet Joker Parier -->
                <b-tab title="Joker - Gamble" key="joker__parier">
                  <b-card-title>Joker - Gamble</b-card-title>
                  <hr class="pb-5" />

                  <b-card-text>
                    <!-- Nombre de paris: defaultJokerGambleCount -->
                    <b-form-group label="Number of Double jabs" label-cols="3">
                      <input
                        type="number"
                        v-model="game.defaultJokerGambleCount"
                        class="form-control form-control-sm" />
                    </b-form-group>

                    <!-- Image Joker - Parier: defaultJokerGambleImage -->
                    <b-form-group label="Joker image - Double jab" label-cols="3">
                      <ImageSelector
                        :initial-image="{
                          name: game.defaultJokerGambleImage,
                          path: game.defaultJokerGambleImageUrl,
                        }"
                        buttons-position="right"
                        empty-container-action="import"
                        :disabled="loading"
                        @file-uploaded="onImageUploaded($event)"
                        @file-selected="
                          onFileSelected(
                            $event,
                            'defaultJokerGambleImage',
                            'defaultJokerGambleImagePath',
                            'defaultJokerGambleImageUrl'
                          )
                        " />
                    </b-form-group>

                    <!-- Image utilisation joker - Parier: jokerOverviewGambleImage -->
                    <b-form-group label="Image use joker - Double jab" label-cols="3">
                      <ImageSelector
                        :initial-image="{
                          name: game.jokerOverviewGambleImage,
                          path: game.jokerOverviewGambleImageUrl,
                        }"
                        buttons-position="right"
                        empty-container-action="import"
                        :disabled="loading"
                        @file-uploaded="onImageUploaded($event)"
                        @file-selected="
                          onFileSelected(
                            $event,
                            'jokerOverviewGambleImage',
                            'jokerOverviewGambleImagePath',
                            'jokerOverviewGambleImageUrl'
                          )
                        " />
                    </b-form-group>

                    <!-- Image de fond - Parier: pointsGambleBackImage -->
                    <b-form-group label="Background image - Double jab" label-cols="3">
                      <ImageSelector
                        :initial-image="{
                          name: game.pointsGambleBackImage,
                          path: game.pointsGambleBackImageUrl,
                        }"
                        buttons-position="right"
                        empty-container-action="import"
                        :disabled="loading"
                        @file-uploaded="onImageUploaded($event)"
                        @file-selected="
                          onFileSelected(
                            $event,
                            'pointsGambleBackImage',
                            'pointsGambleBackImagePath',
                            'pointsGambleBackImageUrl'
                          )
                        " />
                    </b-form-group>

                    <!-- Image de fond - Parieur: pointsGamblerBackImage -->
                    <b-form-group label="Background image - Double jabber" label-cols="3">
                      <ImageSelector
                        :initial-image="{
                          name: game.pointsGamblerBackImage,
                          path: game.pointsGamblerBackImageUrl,
                        }"
                        buttons-position="right"
                        empty-container-action="import"
                        :disabled="loading"
                        @file-uploaded="onImageUploaded($event)"
                        @file-selected="
                          onFileSelected(
                            $event,
                            'pointsGamblerBackImage',
                            'pointsGamblerBackImagePath',
                            'pointsGamblerBackImageUrl'
                          )
                        " />
                    </b-form-group>
                  </b-card-text>
                </b-tab>

                <!-- Onglet Joker Voler -->
                <b-tab title="Joker - Hook" key="joker__voler">
                  <b-card-title>Joker - Hook</b-card-title>
                  <hr class="pb-5" />

                  <b-card-text>
                    <!-- Nombre de vols: defaultJokerStealCount -->
                    <b-form-group label="Number of hooks" label-cols="3">
                      <input type="number" v-model="game.defaultJokerStealCount" class="form-control form-control-sm" />
                    </b-form-group>

                    <!-- Image Joker - Vol: defaultJokerStealImage -->
                    <b-form-group label="Joker image - Hook" label-cols="3">
                      <ImageSelector
                        :initial-image="{
                          name: game.defaultJokerStealImage,
                          path: game.defaultJokerStealImageUrl,
                        }"
                        buttons-position="right"
                        empty-container-action="import"
                        :disabled="loading"
                        @file-uploaded="onImageUploaded($event)"
                        @file-selected="
                          onFileSelected(
                            $event,
                            'defaultJokerStealImage',
                            'defaultJokerStealImagePath',
                            'defaultJokerStealImageUrl'
                          )
                        " />
                    </b-form-group>

                    <!-- Image utilisation joker - Vol: jokerOverviewStealImage -->
                    <b-form-group label="Image use joker - Hook" label-cols="3">
                      <ImageSelector
                        :initial-image="{
                          name: game.jokerOverviewStealImage,
                          path: game.jokerOverviewStealImageUrl,
                        }"
                        buttons-position="right"
                        empty-container-action="import"
                        :disabled="loading"
                        @file-uploaded="onImageUploaded($event)"
                        @file-selected="
                          onFileSelected(
                            $event,
                            'jokerOverviewStealImage',
                            'jokerOverviewStealImagePath',
                            'jokerOverviewStealImageUrl'
                          )
                        " />
                    </b-form-group>

                    <!-- Image de fond - Voleur: pointsStealBackImage -->
                    <b-form-group label="Background image - Hook" label-cols="3">
                      <ImageSelector
                        :initial-image="{
                          name: game.pointsStealBackImage,
                          path: game.pointsStealBackImageUrl,
                        }"
                        buttons-position="right"
                        empty-container-action="import"
                        :disabled="loading"
                        @file-uploaded="onImageUploaded($event)"
                        @file-selected="
                          onFileSelected(
                            $event,
                            'pointsStealBackImage',
                            'pointsStealBackImagePath',
                            'pointsStealBackImageUrl'
                          )
                        " />
                    </b-form-group>

                    <!-- Image de fond - Volé: pointsStolenBackImage -->
                    <b-form-group label="Background image - Hooked" label-cols="3">
                      <ImageSelector
                        :initial-image="{
                          name: game.pointsStolenBackImage,
                          path: game.pointsStolenBackImageUrl,
                        }"
                        buttons-position="right"
                        empty-container-action="import"
                        :disabled="loading"
                        @file-uploaded="onImageUploaded($event)"
                        @file-selected="
                          onFileSelected(
                            $event,
                            'pointsStolenBackImage',
                            'pointsStolenBackImagePath',
                            'pointsStolenBackImageUrl'
                          )
                        " />
                    </b-form-group>
                  </b-card-text>
                </b-tab>
              </b-tabs>
            </b-card-text>
          </b-tab>
        </b-tabs>
      </b-card>
    </b-container>

    <ModalConfirm ref="deleteModalConfirm"></ModalConfirm>
  </b-modal>
</template>

<script>
  import _ from 'lodash'
  import VueTypeaheadBootstrap from 'vue-typeahead-bootstrap'
  import AudioSelector from '@/components/file/AudioSelector.vue'
  import ImageSelector from '@/components/file/ImageSelector.vue'
  import draggable from 'vuedraggable'
  import ModalConfirm from '@/components/core/ModalConfirm.vue'
  import HandleCard from '@/components/game/HandleCard.vue'
  import { getLanguageSelectOptions } from '@/constants/languages'

  export default {
    name: 'GameFormModal',
    components: {
      VueTypeaheadBootstrap,
      draggable,
      AudioSelector,
      ImageSelector,
      ModalConfirm,
      HandleCard,
    },
    props: {
      show: {
        type: Boolean,
        default: false,
      },
      loading: {
        type: Boolean,
        default: false,
      },
      errors: {
        type: Object,
        default: () => {},
      },
      gameToEdit: {
        type: Object,
        default: null,
      },
      themes: {
        type: Array,
        default: () => [],
      },
      levels: {
        type: Array,
        default: () => [],
      },
      defaultHandles: {
        type: Object,
        default: () => {},
      },
      videos: {
        type: Array,
        default: () => [],
      },
      generalFilters: {
        type: Object,
        default: () => ({
          versions: [],
          language: 'fr',
        }),
      },
    },
    data() {
      return {
        defaultGameData: {
          isDefault: true,
          name: '',
          description: '',
          display_name: true,
          themes: [],
          defaultLevel: '',
          language: this.generalFilters.language || 'fr',
          audio_launch_url: '',
          audio_launch_url_path: '',
          audio: '',
          audioPath: '',

          // Manches
          handles: [],

          // Images par défaut
          presentation_image_name: '',
          presentation_image_url: '',
          deskDisplayImage: '',
          deskDisplayImagePath: '',
          defaultDisplay: '',
          defaultDisplayPath: '',
          questionDisplay: '',
          questionDisplayPath: '',
          scoreDisplay: '',
          scoreDisplayPath: '',

          // Textes
          waitOtherPlayers: '',
          waitClickHereBtn: '',
          waitFighterLabel: '',
          availableJokers: '',
          noJokerSent: '',
          noJokerReceived: '',
          uppercutSent: '',
          uppercutReceived: '',
          parierSent: '',
          parierReceived: '',
          volerSent: '',
          volerReceived: '',
          questionScoresTableRound: '',
          questionScoresTablePointsWin: '',
          questionScoresTablePointsLost: '',
          questionScoresTablePointsTotal: '',
          questionsLeft: '',
          resultTotalPointsText: '',
          trueText: '',
          falseText: '',
          pauseText: '',
          rightAnswer: '',
          wrongAnswer: '',
          rightAnswerAlt: '',
          wrongAnswerAlt: '',
          almostAnswerOne: '',
          almostAnswerTwo: '',
          almostAnswerThree: '',
          blindtestFirst: '',
          blindtestSecond: '',
          blindtestThird: '',
          blindtestTooLate: '',
          finalScoreJokerWinnerPoints: '',
          finalScoreJokerLoserPoints: '',
          finalScoreJokerNone: '',

          // Jokers - General
          opponentChoiceImage: null,
          opponentChoiceImagePath: null,
          defaultJokerUnavailableImage: null,
          defaultJokerUnavailableImagePath: null,
          noJokerImage: null,
          noJokerImagePath: null,
          pointNoneBackImage: null,
          pointNoneBackImagePath: null,
          // Jokers - Uppercut
          defaultJokerUppercutImage: null,
          defaultJokerUppercutImagePath: null,
          jokerOverviewUppercutImage: null,
          jokerOverviewUppercutImagePath: null,
          pointsUppercutBackImage: null,
          pointsUppercutBackImagePath: null,
          pointsUppercuttedBackImage: null,
          pointsUppercuttedBackImagePath: null,
          // Jokers - Parier
          defaultJokerGambleImage: null,
          defaultJokerGambleImagePath: null,
          jokerOverviewGambleImage: null,
          jokerOverviewGambleImagePath: null,
          pointsGambleBackImage: null,
          pointsGambleBackImagePath: null,
          pointsGamblerBackImage: null,
          pointsGamblerBackImagePath: null,
          // Jokers - Voler
          defaultJokerStealImage: null,
          defaultJokerStealImagePath: null,
          jokerOverviewStealImage: null,
          jokerOverviewStealImagePath: null,
          pointsStealBackImage: null,
          pointsStealBackImagePath: null,
          pointsStolenBackImage: null,
          pointsStolenBackImagePath: null,
        },
        game: _.cloneDeep(this.defaultGameData),
        originalGame: {},
        typeaheadThemeModel: '',

        handleTab: 0,
        drag: false,
      }
    },
    computed: {
      /** Indique si le modal est ouvert */
      showModal: {
        get() {
          return this.show
        },
        set(value) {
          this.$emit('update:show', value)
        },
      },

      /** Indique si on est en mode édition ou création d'un jeu */
      editMode() {
        return !!this.gameToEdit
      },

      /** Indique s'il y a des modifications non sauvegardées */
      hasUnsavedChanges() {
        return !_.isEmpty(this.gameDifferences)
      },

      /** Obtenir les différences entre le jeu à modifier et le jeu modifié */
      gameDifferences() {
        if (this.editMode == false) {
          return false
        }

        if (_.isEmpty(this.game._id)) {
          return false
        }

        return _.reduce(
          this.game,
          (result, value, key) => {
            if (!_.isEqual(value, this.gameToEdit[key])) {
              result[key] = {
                oldValue: _.cloneDeep(this.gameToEdit[key]),
                newValue: _.cloneDeep(value),
              }
            }
            return result
          },
          {}
        )
      },

      /** Retourne le nom localisé du type de manche */
      handlesTypeWithName() {
        return {
          classique: 'Classique',
          binaire: 'Binaire',
          blindtest: 'Blindtest',
          juste_prix: 'Juste Prix',
          score: 'Score',
          score_final: 'Score Final',
        }
      },

      /** Retourne la liste des thèmes qui ne sont pas encore dans le jeu */
      availableThemes() {
        return this.themes.filter((theme) => !this.game.themes.includes(theme))
      },

      /** Retourne la liste des types de manches jouables */
      playableHandleTypes() {
        return Object.keys(this.handlesTypeWithName).filter(
          (h) => h.toLowerCase() !== 'score_final' && h.toLowerCase() !== 'score'
        )
      },

      /** Retourne la liste des types de manches non jouables */
      unplayableHandleTypes() {
        return Object.keys(this.handlesTypeWithName).filter(
          (h) => h.toLowerCase() === 'score_final' || h.toLowerCase() === 'score'
        )
      },

      /** Options du drag & drop */
      dragOptions() {
        return {
          animation: 200,
          group: 'description',
          disabled: false,
          ghostClass: 'ghost',
        }
      },

      /** Options de langues pour le sélecteur */
      languageOptions() {
        const options = getLanguageSelectOptions('en')
        return Object.keys(options).map((code) => ({
          value: code,
          text: options[code],
        }))
      },
    },
    watch: {
      gameToEdit: {
        immediate: true,
        handler(game) {
          if (game) {
            this.game = _.cloneDeep(game)
          } else {
            this.game = _.cloneDeep(this.defaultGameData)
          }
        },
      },
      'generalFilters.language'(newLang) {
        // Mettre à jour la langue du jeu uniquement si on n'est pas en mode édition
        if (!this.gameToEdit && newLang) {
          this.game.language = newLang
        }
      },
    },
    methods: {
      /**
       * Gestion de la soumission du formulaire
       * @param bvModalEvent
       */
      handleSubmit(bvModalEvent) {
        bvModalEvent.preventDefault()

        // Si tout est valide, émet l'événement submit
        this.$emit('submit', this.game)
      },

      /**
       * Ajouter le thème sélectionné au jeu
       * @param event
       */
      addTheme(theme) {
        this.game.themes.push(theme)

        setTimeout(() => {
          // On vide le champ de recherche
          this.typeaheadThemeModel = ''

          // On réouvre le typeahead
          this.$refs.typeaheadThemes.$refs.input.blur()
          this.$refs.typeaheadThemes.$refs.input.focus()
        })
      },

      /**
       * Supprimer le thème sélectionné du jeu
       * @param theme
       */
      removeTheme(theme) {
        this.game.themes = this.game.themes.filter((t) => t !== theme)
      },

      /** Sauvegarde les informations du jeu original */
      setOriginalGame() {
        this.originalGame = _.cloneDeep(this.game)
      },

      /**
       * Gestion de la sélection d'un fichier audio
       * @param file
       */
      onAudioUploaded(file) {
        this.$emit('audioUploaded', file)
      },

      /**
       * Gestion de la sélection d'une image
       * @param file
       */
      onImageUploaded(file) {
        this.$emit('imageUploaded', file)
      },

      /**
       * Ajoute le fichier selectionné au jeu de manière dynamique
       * @param file
       * @param fieldname
       * @param fieldpath
       */
      onFileSelected(file, fieldname = null, fieldpath = null, fieldurl = null) {
        if (!file) {
          if (fieldname) this.$set(this.game, fieldname, null)
          if (fieldpath) this.$set(this.game, fieldpath, null)
          if (fieldurl) this.$set(this.game, fieldurl, null)
          return
        }

        if (fieldname) this.$set(this.game, fieldname, file.name)
        if (fieldpath) this.$set(this.game, fieldpath, file.path)
        if (fieldurl) this.$set(this.game, fieldurl, file.url)
      },

      /**
       * Gestion du drag & drop
       */
      dragStart() {
        this.drag = true
      },
      dragEnd(event) {
        const oldIndex = event.oldIndex
        const newIndex = event.newIndex

        this.refreshHandleTab(oldIndex, newIndex)
        this.drag = false
      },

      /**
       * Réorganise l'ordre des manches
       * @param oldIndex
       * @param newIndex
       */
      refreshHandleTab(oldValue, newValue) {
        let newHandleTab = this.handleTab
        if (this.handleTab === oldValue) {
          newHandleTab = newValue
        } else {
          if (oldValue > this.handleTab && newValue <= this.handleTab) {
            newHandleTab++
          } else if (oldValue < this.handleTab && newValue >= this.handleTab) {
            newHandleTab--
          }
        }

        setTimeout(() => {
          this.$set(this, 'handleTab', newHandleTab)
        }, 1)
      },

      /**
       * Retourne les valeurs par défaut de la manche sélectionnée par son type
       * @param type
       * @returns
       */
      getDefaultHandleFromType(type) {
        return this.defaultHandles[type]
      },

      /**
       * Ajoute une manche
       * @param type
       */
      addHandle(type) {
        if (!this.game.handles) {
          this.game.handles = []
        }

        let selectedHandle = this.getDefaultHandleFromType(type)
        if (!selectedHandle) {
          this.$notify({
            title: 'Erreur',
            text: 'Manche non trouvée',
            type: 'error',
          })
          return
        }

        // On génère un id unique pour la manche
        selectedHandle.id = Math.random().toString(36).substr(2, 9)

        // Dans le cas où l'on a déjà ajouté une manche "Score Final",
        // On ajoute la manche juste avant la manche "Score Final"
        // Sinon, on ajoute la manche à la fin des manches
        let handles = this.game.handles
        const scoreFinalHandleIndex = handles.findIndex((handle) => handle.type === 'Score Final')

        if (scoreFinalHandleIndex !== -1) {
          handles.splice(scoreFinalHandleIndex, 0, selectedHandle)
        } else {
          handles.push(selectedHandle)
        }

        // On ajoute la manche au jeu
        this.$set(this.game, 'handles', handles)

        // On met à jour le handleTab
        let handleTab = this.handleTab
        if (scoreFinalHandleIndex !== -1) {
          handleTab = scoreFinalHandleIndex
        } else {
          handleTab = handles.length - 1
        }

        // Si handleTab utilise toujours le meme index, on force la reactivité en changeant rapidement l'index au suivant
        if (handleTab === this.handleTab) {
          this.$set(this, 'handleTab', handleTab + 1)
        }

        setTimeout(() => {
          this.$set(this, 'handleTab', handleTab)
        }, 5)
      },

      /**
       * Supprime une manche
       * @param index
       */
      async deleteHandle(index) {
        const confirmed = await this.$refs.deleteModalConfirm.open(
          'Etes-vous sûr ?',
          'Voulez-vous vraiment supprimer cette manche ?',
          {}
        )

        if (!confirmed) {
          return
        }

        let handles = this.game.handles
        handles.splice(index, 1)
        this.$set(this.game, 'handles', handles)

        // Update the handleTab
        let handleTab = this.handleTab
        if (handleTab >= this.game.handles.length) {
          handleTab = 0
        } else if (handleTab > index) {
          handleTab--
        }

        setTimeout(() => {
          this.$set(this, 'handleTab', handleTab)
        }, 5)
      },

      /**
       * Gestion de la modification d'une manche
       * @param index
       * @param event
       */
      handleInput(index, event) {
        this.$set(this.game.handles, index, event)
      },

      /**
       * Gestion du changement de type d'une manche
       * @param index
       * @param type
       */
      handleTypeChanged({ index, type }) {
        let handle = this.game.handles[index]
        if (!handle) return

        let defaultHandle = this.getDefaultHandleFromType(type)
        if (defaultHandle) {
          handle = {
            id: handle.id,
            ...defaultHandle,
          }
        }

        let gameHandles = this.game.handles
        gameHandles.splice(index, 1, handle)
        this.$set(this.game, 'handles', gameHandles)
      },
    },
  }
</script>
